import { LearnerProfileById } from '@/api/common';
import AvatarProfile from '@/components/AvatarProfile';
import Achievement from '@/components/ModalInfoProfileRanking/Achievement';
import Awards from '@/components/ModalInfoProfileRanking/Awards';
import ProfileRanking from '@/components/ModalInfoProfileRanking/ProfileRanking';
import ProfileUserAttempt from '@/components/ModalInfoProfileRanking/ProfileUserAttempt';
import { DinRoundProFont } from '@/lib/utils';
import { useQuery } from '@apollo/client';
import { Modal as BaseModal } from '@flyer/components';
import time from '@flyer/utils/time';
import useTranslation from 'next-translate/useTranslation';
import React, { useCallback, useMemo } from 'react';
import { twMerge } from 'tailwind-merge';
import iconClose from '@/assets/images/icon-close.png';
import iconCoin from '@/assets/icons/icon-coin.png';
import iconWisdomStone from '@/assets/icons/icon_wisdom_stone.png';
import iconTotalScore from '@/assets/icons/icon_energy_yellow.png';
import iconTime from '@/assets/icons/icon_stopwatch.png';
import iconBooks from '@/assets/icons/icon_book.png';
import iconTarget from '@/assets/icons/icon_target.png';

import Image from 'next/image';

export interface ModalProps {
  open: boolean;
  onClose: (o: boolean) => void;
  profileId: string;
  isMobile: boolean;
}

enum DataTab {
  'report' = 'report',
  'userAttempt' = 'userAttempt',
}

export default function ModalInfoProfileRanking(props: ModalProps) {
  const { open, onClose, profileId, isMobile } = props;

  const { t, lang } = useTranslation('common');

  const [currentTab, setCurrentTab] = React.useState<DataTab>(DataTab.report);

  const { data: dataProfile, fetchMore: fetchMoreAttempt } = useQuery(LearnerProfileById, {
    variables: {
      learnerProfileId: profileId,
      first: 25,
      filterBy: {
        submitted: true,
      },
    },
    skip: !profileId,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
  });

  const handlerFetchMoreAttempt = useCallback(() => {
    if (dataProfile?.learnerProfile?.userAttempts?.pageInfo?.hasNextPage) {
      void fetchMoreAttempt({
        variables: {
          first: 25,
          after: dataProfile?.learnerProfile?.userAttempts?.pageInfo?.endCursor,
        },
      });
    }
  }, [
    dataProfile?.learnerProfile?.userAttempts?.pageInfo?.endCursor,
    dataProfile?.learnerProfile?.userAttempts?.pageInfo?.hasNextPage,
    fetchMoreAttempt,
  ]);

  const listInfoUser = useMemo(() => {
    const durationFormat = time
      .duration(dataProfile?.learnerProfile?.attemptStats?.studyTime || '0')
      .asHours()
      .toFixed(2);
    // Đá tri thức chỉ dành cho học sinh thuộc lớp học, không có lớp thì không hiển thị ô này
    const hasClass = Boolean(dataProfile?.learnerProfile?.classStudent?.id);

    return [
      {
        icon: iconCoin,
        title: 'Coin',
        value: dataProfile?.learnerProfile?.coin || 0,
      },
      ...(hasClass
        ? [
            {
              icon: iconWisdomStone,
              title: t('ranking.wisdom_stone'),
              value: dataProfile?.learnerProfile?.wisdomStoneBalance || 0,
            },
          ]
        : []),
      {
        icon: iconTotalScore,
        title: t('ranking.total_points'),
        value: dataProfile?.learnerProfile?.attemptStats?.totalGrade,
      },
      {
        icon: iconTime,
        title: t('ranking.Total_doing_test_time'),
        value: durationFormat,
      },
      {
        icon: iconBooks,
        title: t('ranking.completed_test'),
        value: dataProfile?.learnerProfile?.attemptStats?.completedTest,
      },
      {
        icon: iconTarget,
        title: t('ranking.accuracy'),
        value: dataProfile?.learnerProfile?.attemptStats?.accuracy,
      },
    ];
  }, [
    dataProfile?.learnerProfile?.attemptStats?.accuracy,
    dataProfile?.learnerProfile?.attemptStats?.completedTest,
    dataProfile?.learnerProfile?.attemptStats?.studyTime,
    dataProfile?.learnerProfile?.attemptStats?.totalGrade,
    dataProfile?.learnerProfile?.classStudent?.id,
    dataProfile?.learnerProfile?.coin,
    dataProfile?.learnerProfile?.wisdomStoneBalance,
    t,
  ]);

  return (
    <BaseModal
      open={open}
      centered
      lockScroll={false}
      onClose={onClose}
      className={twMerge(
        'outline-none w-full md:w-[720px] lg:w-[1012px] relative rounded-[10px] text-center max-md:!scale-75',
        DinRoundProFont.className,
      )}
      disableBackdrop={false}
    >
      <div className="w-full bg-white rounded-[10px] overflow-y-auto min-h-[450px] max-md:max-h-[550px]">
        <div
          aria-hidden
          onClick={() => onClose(false)}
          className="absolute -right-4 -top-4 w-[71px] h-[71px] cursor-pointer"
        >
          <Image src={iconClose} alt="close" className="h-full w-full object-contain" />
        </div>
        <div
          data-test-id="modal-info-user-header"
          className="bg-[#321F87] h-[270px] md:h-[224px] w-full flex flex-col justify-between"
        >
          <div className="h-[238px] md:h-[190px] w-full flex items-start md:items-center px-8">
            <AvatarProfile
              attendance={dataProfile?.learnerProfile?.star || 0}
              avatarUrl={dataProfile?.learnerProfile?.avatarUrl}
              frameImageUrl={dataProfile?.learnerProfile?.frameImage?.url}
              displayName={dataProfile?.learnerProfile?.displayName}
              isMobile={isMobile}
            />
          </div>
          <div
            data-test-id="list-tab-data"
            className="flex items-center max-md:w-full justify-between"
          >
            {Object.values(DataTab).map((tab) => {
              const isActive = currentTab === tab;
              return (
                <div aria-hidden key={tab} className="w-1/2" onClick={() => setCurrentTab(tab)}>
                  <span
                    className={twMerge(
                      'text-lg md:text-[20px] font-bold uppercase cursor-pointer text-white',
                      DinRoundProFont.className,
                    )}
                  >
                    {t(`ranking.${tab}`)}
                  </span>
                  {isActive && (
                    <div className="bg-[#BBACFF]  h-[3px] w-full transition-all duration-500" />
                  )}
                </div>
              );
            })}
          </div>
        </div>
        {currentTab === 'report' ? (
          <div className="min-h-[378px]">
            <div
              data-test-id="list-row-user"
              className={twMerge(
                'grid grid-cols-2 md:grid-cols-3 gap-4 p-4',
                listInfoUser.length === 6 ? 'lg:grid-cols-6' : 'lg:grid-cols-5',
              )}
            >
              {listInfoUser?.map((info) => {
                return (
                  <div
                    key={info.title}
                    className="flex flex-col items-start gap-1 border-solid border-[1px] border-[#EFEFEF] bg-[#F9F8FF] rounded-[8px] p-3"
                  >
                    <Image
                      alt="flyer"
                      src={info.icon}
                      className="w-[34px] h-[34px] object-contain"
                    />
                    <p className="text-[#321F87] text-[18px] font-normal">{info.title}</p>
                    <p className="text-[#5E5E5E] text-xl font-bold">
                      {info?.value?.toLocaleString(lang) || 0}
                    </p>
                  </div>
                );
              })}
            </div>
            <div className="flex flex-col md:flex-row gap-4 px-4 pb-4 transition-all duration-500">
              <ProfileRanking profileId={profileId} />
              <Achievement dataListAchievement={dataProfile?.learnerProfile?.achievements} />
            </div>
            <Awards profileId={profileId} />
          </div>
        ) : (
          <div className="flex flex-row gap-4 px-4 pb-4 h-[378px]">
            <ProfileUserAttempt
              fetchMoreAttempts={handlerFetchMoreAttempt}
              userAttempt={dataProfile?.learnerProfile?.userAttempts}
            />
          </div>
        )}
      </div>
    </BaseModal>
  );
}
